import React, { useEffect, useState, useRef, useCallback } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./NewsCarousel.css";
import { useNavigate } from "react-router-dom";
import DeleteButtons from "../icon/deleteicon";
import EditNewsPost from "./EditNewsPost";
import { getApiBaseUrl } from "../../config/api";

const NEWS_PAGE_SIZE = 4;

const NextArrow = ({ onClick, loadingMore, onNeedMore }) => (
  <button
    type="button"
    className={`news-arrow next${loadingMore ? " is-loading" : ""}`}
    onClick={async (e) => {
      if (onNeedMore) {
        const handled = await onNeedMore();
        if (handled) return;
      }
      onClick?.(e);
    }}
    aria-label="Next"
    aria-busy={loadingMore}
  >
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 6l6 6-6 6" />
    </svg>
  </button>
);

const PrevArrow = ({ onClick }) => (
  <button type="button" className="news-arrow prev" onClick={onClick} aria-label="Previous">
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M15 6l-6 6 6 6" />
    </svg>
  </button>
);

const NewsCarousel = ({ user, showViewAll = true }) => {
  const [news, setNews] = useState([]);
  const [slidesToShow, setSlidesToShow] = useState(3);
  const [visible, setVisible] = useState(true);
  const [editingPost, setEditingPost] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [nextPage, setNextPage] = useState(2);
  const [loadingMore, setLoadingMore] = useState(false);
  const sectionRef = useRef(null);
  const sliderRef = useRef(null);
  const currentSlideRef = useRef(0);
  const pendingAdvanceRef = useRef(false);
  const loadingMoreRef = useRef(false);
  const navigate = useNavigate();
  const API_URL = getApiBaseUrl();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setVisible(true);
        });
      },
      { threshold: 0.15, rootMargin: "0px 0px -40px 0px" }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const fetchNewsPage = useCallback(async (pageNum) => {
    const params = new URLSearchParams({
      page: String(pageNum),
      limit: String(NEWS_PAGE_SIZE),
    });
    const res = await fetch(`${API_URL}/get_newslist.php?${params.toString()}`);
    const data = await res.json();
    const items = data.success && Array.isArray(data.news) ? data.news : [];
    return {
      items,
      hasMore: Boolean(data.has_more),
      nextPage: typeof data.next_page === "number" ? data.next_page : pageNum + 1,
    };
  }, [API_URL]);

  const fetchNews = useCallback(async () => {
    try {
      const page = await fetchNewsPage(1);
      const uniqueNews = Array.from(
        new Map(page.items.map((item) => [item.id, item])).values()
      );
      setNews(uniqueNews);
      setHasMore(page.hasMore);
      setNextPage(page.nextPage);
      currentSlideRef.current = 0;
    } catch (err) {
      console.error("Error fetching news:", err);
      setNews([]);
      setHasMore(false);
    }
  }, [fetchNewsPage]);

  const loadMore = useCallback(async () => {
    if (!hasMore || loadingMoreRef.current) return [];
    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      const page = await fetchNewsPage(nextPage);
      if (!page.items.length) {
        setHasMore(false);
        return [];
      }
      setNews((prev) => {
        const seen = new Set(prev.map((item) => item.id));
        const extra = page.items.filter((item) => !seen.has(item.id));
        return extra.length ? [...prev, ...extra] : prev;
      });
      setHasMore(page.hasMore);
      setNextPage(page.nextPage);
      return page.items;
    } catch (err) {
      console.error("Error loading more news:", err);
      return [];
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, [fetchNewsPage, hasMore, nextPage]);

  useEffect(() => {
    fetchNews();
  }, [fetchNews]);

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this post?")) return;

    try {
      const res = await fetch(`${API_URL}/delete_newspost.php?id=${id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id }),
      });

      const data = await res.json();
      if (data.success) {
        alert("Post deleted successfully!");
        fetchNews();
      } else {
        alert(data.message || "Failed to delete post");
      }
    } catch (err) {
      console.error("Error deleting post:", err);
      alert("An error occurred while deleting post.");
    }
  };

  useEffect(() => {
    const updateSlides = () => {
      if (window.innerWidth < 768) {
        setSlidesToShow(1);
      } else if (window.innerWidth < 1100) {
        setSlidesToShow(2);
      } else {
        setSlidesToShow(3);
      }
    };

    updateSlides();
    window.addEventListener("resize", updateSlides);
    return () => window.removeEventListener("resize", updateSlides);
  }, []);

  const safeSlidesToShow = Math.max(1, Math.min(slidesToShow, news.length || 1));
  const allowInfinite = !hasMore && news.length > safeSlidesToShow;
  const showArrows = news.length > safeSlidesToShow || hasMore;

  useEffect(() => {
    if (!pendingAdvanceRef.current) return;
    pendingAdvanceRef.current = false;
    requestAnimationFrame(() => {
      sliderRef.current?.slickNext();
    });
  }, [news]);

  const handleNeedMore = async () => {
    const lastStart = Math.max(0, news.length - safeSlidesToShow);
    const atEnd = currentSlideRef.current >= lastStart;
    if (!atEnd) return false;
    if (hasMore || loadingMoreRef.current) {
      pendingAdvanceRef.current = true;
      await loadMore();
      return true;
    }
    return false;
  };

  const settings = {
    dots: news.length > 1,
    arrows: showArrows,
    infinite: allowInfinite,
    speed: 450,
    slidesToShow: safeSlidesToShow,
    slidesToScroll: 1,
    swipe: true,
    touchThreshold: 8,
    nextArrow: (
      <NextArrow loadingMore={loadingMore} onNeedMore={handleNeedMore} />
    ),
    prevArrow: <PrevArrow />,
    beforeChange: (_oldIndex, newIndex) => {
      currentSlideRef.current = newIndex;
    },
    afterChange: (index) => {
      currentSlideRef.current = index;
      const lastStart = Math.max(0, news.length - safeSlidesToShow);
      if (hasMore && index >= lastStart) {
        loadMore();
      }
    },
    appendDots: (dots) => (
      <div className="news-dots-wrap">
        <ul className="news-dots">{dots}</ul>
      </div>
    ),
    customPaging: () => <button type="button" className="news-dot" aria-label="Go to slide" />,
  };

  const openArticle = (id) => navigate(`/news/${id}`);

  return (
    <>
      {editingPost && (
        <EditNewsPost
          post={editingPost}
          onClose={() => setEditingPost(null)}
          onUpdated={fetchNews}
        />
      )}
      <section
        ref={sectionRef}
        className={`news-wrapper news-wrapper--animate${visible ? " news-wrapper--visible" : ""}`}
        aria-labelledby="news-highlights-heading"
      >
        <div className="news-section-bg" aria-hidden="true">
          <span className="news-blob news-blob--1" />
          <span className="news-blob news-blob--2" />
          <span className="news-plane">
            <svg viewBox="0 0 64 64" fill="none" aria-hidden="true">
              <path
                d="M8 32c18-6 28-10 40-20L36 44l-8-10-12 4 4-6z"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinejoin="round"
              />
              <path
                d="M48 12c6 4 10 10 12 16"
                stroke="currentColor"
                strokeWidth="1.4"
                strokeDasharray="2 3"
                strokeLinecap="round"
              />
            </svg>
          </span>
        </div>

        <header className="news-header">
          <p className="news-eyebrow">Stay in the loop</p>
          <h2 id="news-highlights-heading" className="news-heading">
            <span>News and</span>{" "}
            <span className="news-heading-highlight">Highlights</span>
          </h2>
          <div className="news-heading-accent" aria-hidden="true" />
          <p className="news-subhead">
            Explore the latest updates, stories, and achievements.
          </p>
        </header>

        <div className="news-carousel" data-visible={safeSlidesToShow}>
          {news.length === 0 ? (
            <p className="news-empty">No news posts yet.</p>
          ) : (
            <Slider ref={sliderRef} key={`news-${safeSlidesToShow}`} {...settings}>
              {news.map((item) => {
                const mediaList = Array.isArray(item.media) ? item.media : [];
                const firstMedia = mediaList.length > 0 ? mediaList[0].url : null;
                const photoCount = mediaList.filter(
                  (m) => m?.url && !/\.(mp4|webm|mov)$/i.test(m.url)
                ).length;

                const formattedDate = new Date(item.created_at).toLocaleDateString(undefined, {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                });

                return (
                  <article key={item.id} className="news-card">
                    <div className="news-card-frame">
                      <div className="news-card-media">
                        {firstMedia ? (
                          /\.(mp4|webm|mov)$/i.test(firstMedia) ? (
                            <video
                              className="news-media"
                              src={firstMedia}
                              controls
                              poster="https://fms.techtreeglobal.com/assets/uploads/1758604320_logo.jpg"
                            />
                          ) : (
                            <img src={firstMedia} alt={item.title} className="news-media" />
                          )
                        ) : (
                          <img
                            src="https://fms.techtreeglobal.com/assets/uploads/1758604320_logo.jpg"
                            alt={item.title}
                            className="news-media"
                          />
                        )}

                        <time className="news-card-date" dateTime={item.created_at}>
                          {formattedDate}
                        </time>

                        {photoCount > 1 && (
                          <span className="news-photo-count">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                              <rect x="3" y="5" width="14" height="12" rx="2" />
                              <path d="M7 19h10a2 2 0 0 0 2-2V9" />
                            </svg>
                            {photoCount} photos
                          </span>
                        )}
                      </div>

                      <div className="news-card-panel">
                        <h3 className="news-card-title">{item.title}</h3>
                        <p className="news-text">
                          {item.content.length > 90
                            ? `${item.content.substring(0, 90)}…`
                            : item.content}
                        </p>

                        <div className="news-card-actions">
                          <button
                            type="button"
                            className="details-btnCarousel"
                            onClick={() => openArticle(item.id)}
                          >
                            Read article
                          </button>
                          <button
                            type="button"
                            className="news-external-btn"
                            onClick={() => openArticle(item.id)}
                            aria-label={`Open ${item.title}`}
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M7 17L17 7M17 7H9M17 7v8" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>

                    {user?.role === "admin" && (
                      <div className="news-card-admin">
                        <button
                          type="button"
                          className="news-admin-edit"
                          onClick={() => setEditingPost(item)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="delete-btn"
                          onClick={() => handleDelete(item.id)}
                          aria-label="Delete post"
                        >
                          <DeleteButtons />
                        </button>
                      </div>
                    )}
                  </article>
                );
              })}
            </Slider>
          )}
        </div>

        {showViewAll && news.length > 0 && (
          <div className="news-footer">
            <button
              type="button"
              className="news-view-all"
              onClick={() => navigate("/news")}
            >
              View all news &amp; highlights
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M5 12h14M13 6l6 6-6 6" />
              </svg>
            </button>
          </div>
        )}
      </section>
    </>
  );
};

export default NewsCarousel;
